import { Button, cn } from '@repo/shadcn';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { ButtonGroup } from '@components/ui/button';

import {
    useUpdateNotificationAsReadMutation,
    useUpdateNotificationAsUnreadMutation,
} from '@/features/slice/notification';
import { toastError } from '@/utils';

const ActionCell = ({ row }: CellContext<any, unknown>) => {
    const [updateNotificationAsRead, { isLoading }] =
        useUpdateNotificationAsReadMutation();

    const [updateNotificationAsUnread, { isLoading: isUnreadLoading }] =
        useUpdateNotificationAsUnreadMutation();

    const handleMarkAsRead = async () => {
        try {
            await updateNotificationAsRead(row.original.id).unwrap();
            toast.success('Notification marked as read');
        } catch (err) {
            toastError(err, 'Failed to mark notification as read');
        }
    };

    const handleUnmarkAsRead = async () => {
        try {
            await updateNotificationAsUnread(row.original.id).unwrap();
            toast.success('Notification marked as unread');
        } catch (err) {
            toastError(err, 'Failed to mark notification as unread');
        }
    };

    const isDisabled = row.original.status === 'read';
    return (
        <div className="flex justify-end ">
            <ButtonGroup>
                <Button
                    type={'button'}
                    variant={'default'}
                    size={'sm'}
                    disabled={isDisabled || isLoading}
                    className={'text-sm'}
                    onClick={handleMarkAsRead}
                >
                    {isLoading ? (
                        <Loader2Icon className={'animate-spin'} />
                    ) : (
                        'Read'
                    )}
                </Button>
                <Button
                    type={'button'}
                    className={'text-sm'}
                    variant={'default'}
                    size={'sm'}
                    disabled={!isDisabled || isUnreadLoading}
                    onClick={handleUnmarkAsRead}
                >
                    {isLoading ? (
                        <Loader2Icon className={'animate-spin'} />
                    ) : (
                        'Unread'
                    )}
                </Button>
            </ButtonGroup>
        </div>
    );
};

export const notificationListColumn: ColumnDef<any>[] = [
    {
        accessorKey: 'date',
        header: () => <div className="text-sm font-bold ">Date</div>,
        cell: ({ row }) => {
            const formattedDate = format(
                row.original.created_at,
                'dd/MM/yyyy HH:mm a',
            );

            const isRead = row.original.status === 'read';
            return (
                <div
                    className={cn(
                        'text-sm font-medium',
                        isRead ? 'text-gray-500' : 'text-gray-900',
                    )}
                >
                    {formattedDate}
                </div>
            );
        },
    },

    {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => {
            const isRead = row.original.status === 'read';
            return (
                <div
                    className={cn(
                        'text-sm font-medium ',
                        isRead ? 'text-gray-500' : 'text-gray-900',
                    )}
                >
                    {row.original.title}
                </div>
            );
        },
    },
    {
        accessorKey: 'message',
        header: 'Message',
        cell: ({ row }) => {
            const isRead = row.original.status === 'read';
            return (
                <div
                    className={cn(
                        'text-sm ',
                        isRead ? 'text-gray-500' : 'text-gray-900',
                    )}
                >
                    {row.original.message}
                </div>
            );
        },
    },

    {
        accessorKey: 'actions',
        header: () => <div className={'flex justify-end'}>Actions</div>,
        cell: ActionCell,
    },
];
